import * as amplitude from '@amplitude/analytics-browser'
import {SessionReplayPlugin} from '@amplitude/plugin-session-replay-browser'
import {AppConfig} from '@frontend/AppConfig'
import {useEffect} from 'react'
import {loadJsFile} from 'swayok-react-mdb-ui-kit/helpers/loadJsFile'
import type {AnyObject} from 'swayok-react-mdb-ui-kit/types/Common'
import {AnalyticsService} from '../../services/AnalyticsService'

/**
 * Загрузка скриптов сторонней аналитики ПОСЛЕ монтирования React приложения.
 *
 * Причина: iOS Content Blockers (AdGuard, Crystal, Firefox Focus и др.) сканируют
 * атрибуты src тегов <script> при парсинге <head> и, найдя совпадение с фильтром
 * (например `adsbygoogle.js`, `metrika/tag.js`, `fbevents.js`), могут заблокировать
 * весь origin-контекст вкладки. После этого все последующие fetch/XHR запросы SPA
 * на этой вкладке блокируются до физического pull-to-refresh пользователем.
 *
 * Загрузка через useEffect() гарантирует, что React смонтируется и выполнит
 * все критические API-запросы (пользователь, категории, локации) ДО того, как
 * Content Blocker получит шанс заблокировать origin из-за скриптов аналитики.
 *
 * @see react_app.blade.php — аналитические блоки <script> удалены из <head>
 * @see AnalyticsService.initGlobals() - инициализация глобальных переменных для всех сервисов сбора аналитики.
 */
export function AnalyticsBootstrap() {
    useEffect(() => {
        loadAmplitude()
        loadGoogleAnalytics()
        // loadYandexMetrika()
        loadMetaPixel()
        loadMicrosoftClarity()
        loadGoogleAdSense()
    }, [])

    return null
}

/**
 * Инициализация Amplitude.
 * deviceId и userId передаются в init() для корректной работы сессий.
 */
function loadAmplitude(): void {
    if (!AppConfig.amplitudeApiKey) {
        return
    }
    const sessionReplay = new SessionReplayPlugin({
        deviceId: AppConfig.trackingConfig.trackingId!,
        // 0.0 - 1.0 - доля записываемых сессий. 0 = полностью ручное управление.
        sampleRate: 0,
    })
    AnalyticsService.setSessionReplayHandler(sessionReplay)
    amplitude.add(sessionReplay)
    amplitude.init(AppConfig.amplitudeApiKey, {
        autocapture: false,
        deviceId: AppConfig.trackingConfig.trackingId!,
        userId: AppConfig.userId ? String(AppConfig.userId) : undefined,
        minIdLength: 1, // Минимальная длина deviceId и userId.
    })
}

/**
 * Инициализация Google Analytics 4.
 * Создаём window.dataLayer и gtag-функцию ДО загрузки скрипта, чтобы вызовы
 * gtag() в AnalyticsService не потерялись в момент между инициализацией и загрузкой.
 */
function loadGoogleAnalytics(): void {
    const clientId = AppConfig.googleAnalyticsClientId
    if (!clientId) {
        return
    }
    gtag('js', new Date())
    const config: AnyObject = {
        client_id: AppConfig.trackingConfig.trackingId ?? undefined,
    }
    if (AppConfig.userId) {
        config.user_id = AppConfig.userId
    }
    gtag('config', String(clientId), config)
    void loadJsFile(`https://www.googletagmanager.com/gtag/js?id=${clientId}`)
}

/**
 * Инициализация Yandex Metrika.
 * Создаём очередь window.ym ДО загрузки скрипта — стандартный паттерн Метрики:
 * вызовы ym() до загрузки складываются в ym.a и воспроизводятся после загрузки.
 */
// function loadYandexMetrika(): void {
//     const clientId = AppConfig.yandexMetrikaClientId
//     if (!clientId) {
//         return
//     }
//     if (!window.ym) {
//          move this upper
//         type YmArgs = Parameters<typeof window.ym>
//
//         const ymCallsQueue: YmArgs[] = []
//         const ymQueue = ((...args: YmArgs) => void ymCallsQueue.push(args)) as typeof window.ym
//         // Attach queue metadata so the real metrika.js script can replay queued calls.
//         Object.assign(ymQueue, {a: ymCallsQueue, l: Date.now()})
//         window.ym = ymQueue
//     }
//     window.ym(clientId, 'init', {
//         defer: true,
//         clickmap: true,
//         trackLinks: true,
//         accurateTrackBounce: true,
//     })
//     void loadJsFile('https://mc.yandex.ru/metrika/tag.js')
// }

/**
 * Инициализация Meta Pixel.
 * Создаём очередь window.fbq ДО загрузки скрипта — стандартный паттерн Meta Pixel.
 */
function loadMetaPixel(): void {
    const pixelId = AppConfig.metaPixelId
    if (!pixelId) {
        return
    }
    window.fbq?.('init', pixelId, {
        external_id: AppConfig.trackingConfig.trackingId ?? undefined,
    })
    void loadJsFile('https://connect.facebook.net/en_US/fbevents.js')
}

/**
 * Загрузка Microsoft Clarity.
 * Скрипт Clarity самостоятельно инициализирует window.clarity при загрузке.
 */
function loadMicrosoftClarity(): void {
    const clarityId = AppConfig.microsoftClarityId
    if (!clarityId) {
        return
    }
    void loadJsFile(`https://www.clarity.ms/tag/${clarityId}`)
}

/**
 * Загрузка Google AdSense.
 * AdSense загружается последним — это наиболее агрессивно блокируемый скрипт,
 * и его загрузка не должна влиять на работу SPA.
 */
function loadGoogleAdSense(): void {
    const adSenseId = AppConfig.googleAdSenseId
    if (!adSenseId) {
        return
    }
    void loadJsFile(
        `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adSenseId}`
    )
}
